import type { ExperimentClient } from "@amplitude/experiment-js-client"
import { captureException } from "@sentry/browser"
import {
  initialize as launchDarklyInitialize,
  type LDClient,
} from "launchdarkly-js-client-sdk"
import { derived, readable, writable, type Readable } from "svelte/store"

let launchDarklyClient: LDClient

const launchDarklyReady = writable(false)

type AnyTastyLDClient = TastyLDClient<Record<string, FlagDefinition<any>>>

let initializedClient: AnyTastyLDClient | undefined

/**
 * The client captured by `TastyLDClient.initialize`, reachable without Svelte
 * context. It is only set once initialization has settled, so its flag stores
 * already hold real values rather than defaults.
 *
 * Returns undefined before bootstrap completes; callers must fail closed on
 * that. Prefer `getLDClient()` from `@tastyworks/shared-platform-core/app-context`
 * inside components — this exists for module-level code that has no component
 * to read context from.
 */
export function getInitializedLDClient(): AnyTastyLDClient | undefined {
  return initializedClient
}

function publishInitializedClient(client: AnyTastyLDClient) {
  initializedClient = client
}

type InferFlagValue<T> = T extends boolean | MigrationFlag
  ? boolean
  : T extends string
    ? string
    : T extends number
      ? number
      : T extends infer X
        ? X
        : never

/**
 * Flag definition type for LaunchDarkly flags.
 * Used to define flags with their key, default value, and realtime options.
 */
export type FlagDefinition<T> = {
  key: string
  realtime?: boolean
  defaultValue: InferFlagValue<T>
}

type FlagStore<T> = Readable<InferFlagValue<T>>

type FlagStoreMap<T extends Record<string, FlagDefinition<any>>> = {
  [K in keyof T]: FlagStore<T[K]["defaultValue"]>
}

const FLAG_PROVIDER_STARTUP_TIMEOUT_SECONDS = 5

const AMPLITUDE_FETCH_TIMEOUT_MILLIS = 3000

const AMPLITUDE_PLATFORM = "web"

async function rejectAfterStartupTimeout<V>(promise: Promise<V>): Promise<V> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const startupTimeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new Error(
            `No response within ${FLAG_PROVIDER_STARTUP_TIMEOUT_SECONDS}s of startup`
          )
        ),
      FLAG_PROVIDER_STARTUP_TIMEOUT_SECONDS * 1000
    )
  })
  try {
    return await Promise.race([promise, startupTimeout])
  } finally {
    clearTimeout(timer)
  }
}

const amplitudeUserProviderWithoutPageDetails = {
  getUser: () => ({
    url_param: undefined,
    landing_url: undefined,
    referring_url: undefined,
    user_agent: undefined,
  }),
}

export class TastyLDClient<T extends Record<string, FlagDefinition<any>>> {
  public flags!: FlagStoreMap<T>
  private readonly client?: LDClient
  private readonly context: Record<string, any>
  private readonly experimentDeploymentKey?: string
  private readonly amplitudeAnalyticsStarted: boolean
  private experiment?: ExperimentClient

  constructor(
    clientKey: string | undefined,
    context: Record<string, any>,
    private readonly flagValues: T,
    bootstrap?: Record<string, unknown>,
    experimentDeploymentKey?: string,
    amplitudeAnalyticsStarted = false
  ) {
    this.context = context
    this.client = clientKey
      ? launchDarklyInitialize(
          clientKey,
          context,
          bootstrap ? { bootstrap } : undefined
        )
      : undefined
    this.experimentDeploymentKey = experimentDeploymentKey
    this.amplitudeAnalyticsStarted = amplitudeAnalyticsStarted
  }

  private reasonAmplitudeIsSkipped() {
    if (!this.amplitudeAnalyticsStarted) {
      return "analytics did not start"
    }
    return undefined
  }

  private amplitudeTargetingProperties() {
    return {
      platform: AMPLITUDE_PLATFORM,
      isEmployeeDomain: this.context.isEmployeeDomain === true,
    }
  }

  private amplitudeUser() {
    const user_properties = this.amplitudeTargetingProperties()
    if (this.context.anonymous) {
      return { user_id: undefined, user_properties }
    }
    return { user_id: this.context.key, user_properties }
  }

  private async fetchedExperiment(deploymentKey: string) {
    const { Experiment } = await import("@amplitude/experiment-js-client")
    const experiment = Experiment.initializeWithAmplitudeAnalytics(
      deploymentKey,
      {
        fetchTimeoutMillis: AMPLITUDE_FETCH_TIMEOUT_MILLIS,
        throwOnError: true,
        automaticExposureTracking: false,
        automaticFetchOnAmplitudeIdentityChange: false,
        userProvider: amplitudeUserProviderWithoutPageDetails,
      }
    )
    await experiment.setTracksAssignment(false)
    await experiment.fetch(this.amplitudeUser())
    return experiment
  }

  private async fetchAmplitudeVariants() {
    if (!this.experimentDeploymentKey) {
      return
    }
    const skipReason = this.reasonAmplitudeIsSkipped()
    if (skipReason) {
      console.info(`[flags] Amplitude skipped: ${skipReason}`)
      return
    }
    try {
      this.experiment = await rejectAfterStartupTimeout(
        this.fetchedExperiment(this.experimentDeploymentKey)
      )
    } catch (e) {
      this.experiment = undefined
      console.error("Amplitude Experiment fetch failed", e)
    }
  }

  private createClientFlagStores(): FlagStoreMap<T> {
    const stores = {} as FlagStoreMap<T>

    for (const name in this.flagValues) {
      if (this.flagValues.hasOwnProperty(name)) {
        const typedName = name as keyof T
        stores[typedName] = this.createFlagStore(typedName)
      }
    }

    return stores
  }

  private createFlagStore<K extends keyof T>(name: K): any {
    const flagInit = this.flagValues[name]
    const { key, defaultValue, realtime = false } = flagInit

    const baseStore = this.createReadableStore(key, defaultValue, realtime)

    return isMigrationFlag(defaultValue)
      ? derived(baseStore, ($flag) => $flag === MigrationFlag.ON)
      : baseStore
  }

  private createReadableStore<V>(
    key: string,
    defaultValue: V,
    realtime: boolean
  ) {
    const amplitudeValue = this.amplitudeValue(key, defaultValue)

    return readable(this.resolve(key, defaultValue, amplitudeValue), (set) => {
      if (realtime && amplitudeValue === undefined) {
        this.client?.on("change:" + key, set)
      }
    })
  }

  private amplitudeValue<V>(key: string, defaultValue: V): V | undefined {
    const variant = this.experiment?.variant(key)

    if (typeof defaultValue === "boolean") {
      if (variant?.key === "on" || variant?.key === "off") {
        return (variant.key === "on") as V
      }
      return undefined
    }

    if (typeof defaultValue === "number" && variant?.key) {
      const numericKey = Number(variant.key)
      return Number.isFinite(numericKey) ? (numericKey as V) : undefined
    }

    if (Array.isArray(defaultValue) && Array.isArray(variant?.payload)) {
      return variant.payload as V
    }
    return undefined
  }

  private resolve<V>(
    key: string,
    defaultValue: V,
    amplitudeValue: V | undefined
  ): V {
    const launchDarklyValue = this.client
      ? this.client.variation(key, defaultValue)
      : defaultValue

    if (amplitudeValue === undefined) {
      return launchDarklyValue
    }

    console.info(
      `[flags] ${key}: amplitude=${JSON.stringify(amplitudeValue)} launchdarkly=${JSON.stringify(launchDarklyValue)}`
    )
    return amplitudeValue
  }

  async initialize() {
    if (!this.client) {
      console.info("[flags] LaunchDarkly skipped: no client key")
    }
    const launchDarklyReadyPromise = this.client
      ?.waitForInitialization(FLAG_PROVIDER_STARTUP_TIMEOUT_SECONDS)
      .catch((e) => {
        const message =
          "LaunchDarkly client initialization failed. Falling back to default flag values"
        console.error(message, e)

        if (
          e instanceof Error &&
          (e.message.startsWith("network error") ||
            e.name === "LaunchDarklyTimeoutError")
        ) {
          return
        }
        captureException(e)
      })

    await Promise.all([launchDarklyReadyPromise, this.fetchAmplitudeVariants()])

    // Create typed flag stores using the generalized function
    this.flags = this.createClientFlagStores()

    // Establish module-scoped clients for code that runs outside a component
    // tree and so cannot reach the client through Svelte context.
    publishInitializedClient(this)
    if (this.client) {
      launchDarklyClient = this.client
      launchDarklyReady.set(true)
    }
  }

  getContext() {
    return this.context
  }
}

/**
 * Retrieves the value of a feature flag from LaunchDarkly.
 *
 * @param key - The key of the feature flag.
 * @param fnChangeListener - Optional callback function to be called when the feature flag value changes.
 * @returns A Promise that resolves to the value of the feature flag.
 */
async function getFlagValue<T>(
  key: string,
  defaultValue: T,
  fnChangeListener?: (param: T) => void
): Promise<T> {
  if (!launchDarklyClient) {
    throw new Error(
      "LaunchDarkly client not initialized. Call initialize() first."
    )
  }

  const flagValue = await launchDarklyClient.variation(key, defaultValue)

  if (fnChangeListener) {
    launchDarklyClient.on("change:" + key, fnChangeListener)
  }
  return flagValue
}

/**
 * Options for the FlagStore class.
 *
 * @template T - The type of the flag value.
 * @property {T} defaultValue - The default value for the flag.
 * @property {boolean} realtime - Whether to enable real-time updates for the flag.
 */
type FlagStoreOptions<T> = {
  defaultValue?: T
  realtime?: boolean
}

/**
 * Retrieves the value of a feature flag from the flag store.
 *
 * Deliberately not exported: it resolves asynchronously after LaunchDarkly is
 * ready and hands out the default value until then, so a synchronous read can
 * silently see a default. Read flags off `TastyLDClient.flags` instead — those
 * stores are built once initialization has settled.
 *
 * @template T - The type of the flag value.
 * @param {string} key - The key of the feature flag.
 * @param {FlagStoreOptions<T>} options - The options for retrieving the flag value.
 * @returns {Readable<T>} - The readable store containing the flag value.
 */
function flagStore<T>(
  key: string,
  options: FlagStoreOptions<T> = {}
): Readable<T> {
  const { defaultValue = false as T, realtime } = options
  const store = writable(defaultValue)

  const unsubscribe = launchDarklyReady.subscribe((ready) => {
    if (ready) {
      getFlagValue(key, defaultValue, realtime ? store.set : undefined)
        .then(store.set)
        .then(() => unsubscribe()) // Wrap in arrow function to defer evaluation
        .catch((error) => {
          console.warn("LaunchDarkly not configured", error)
          unsubscribe() // Also unsubscribe on error
        })
    }
  })

  return store
}

/**
 * A set of flag values based on launchdarkly defaults for multi-step migration
 */
export enum MigrationFlag {
  OFF = "off",
  ON = "complete",
}

/**
 * Type guard to check if a value is a migration flag
 */
function isMigrationFlag(value: any): value is MigrationFlag {
  return value === MigrationFlag.OFF || value === MigrationFlag.ON
}

const MIGRATION_FLAG_STORE_DEFAULT_OPTIONS: FlagStoreOptions<MigrationFlag> = {
  defaultValue: MigrationFlag.OFF,
  realtime: false,
}

/**
 * Create a flag store for a migration flag represented as a boolean.
 *
 * @param key The key of the feature flag.
 * @param {FlagStoreOptions<T>} options The options for retrieving the flag value.
 * @returns {Readable<boolean>} The derived store containing the boolean flag value.
 */
export function migrationFlagStore(
  key: string,
  options: FlagStoreOptions<MigrationFlag> = {}
) {
  return derived(
    flagStore(key, { ...MIGRATION_FLAG_STORE_DEFAULT_OPTIONS, ...options }),
    ($flag) => $flag === MigrationFlag.ON
  )
}
